import { html, css, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  OPERATOR_NOTIFY_EVENT,
  type OperatorNotifyDetail,
  type OperatorNotifyType,
} from "../operator-notify.js";

type ActiveNotice = OperatorNotifyDetail & { id: string };

@customElement("ead-operator-dialog-host")
export class EadOperatorDialogHost extends LitElement {
  static styles = css`
    :host {
      display: contents;
    }
    .overlay {
      position: fixed;
      inset: 0;
      z-index: 5000;
      background: rgba(0, 0, 0, 0.55);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      pointer-events: auto;
    }
    .dialog {
      width: min(480px, 100%);
      background: var(--bg-surface, #222326);
      border: 1px solid var(--border, #2e2f33);
      border-radius: 12px;
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
      padding: 18px 20px;
    }
    .dialog.success {
      border-left: 4px solid #4caf50;
    }
    .dialog.error {
      border-left: 4px solid #f44336;
    }
    .dialog.warning {
      border-left: 4px solid #ff9800;
    }
    .dialog.info {
      border-left: 4px solid var(--accent, #6ea8fe);
    }
    .header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 10px;
    }
    .title {
      font-size: 15px;
      font-weight: 600;
      color: var(--fg, #e4e5e7);
    }
    .close {
      border: none;
      background: transparent;
      color: var(--muted, #8b8d93);
      font-size: 20px;
      line-height: 1;
      cursor: pointer;
      padding: 0 4px;
      border-radius: 4px;
    }
    .close:hover {
      color: var(--fg, #e4e5e7);
      background: rgba(255, 255, 255, 0.08);
    }
    .message {
      font-size: 13px;
      line-height: 1.55;
      color: var(--muted, #8b8d93);
      white-space: pre-wrap;
    }
    .actions {
      margin-top: 16px;
      display: flex;
      justify-content: flex-end;
    }
    .btn {
      padding: 8px 16px;
      border-radius: 8px;
      border: 1px solid var(--accent, #6ea8fe);
      background: var(--accent, #6ea8fe);
      color: #fff;
      font-size: 13px;
      cursor: pointer;
    }
    .toast-stack {
      position: fixed;
      top: 16px;
      right: 16px;
      z-index: 4999;
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-width: 400px;
      pointer-events: auto;
    }
    .toast {
      background: var(--bg-surface, #222326);
      border: 1px solid var(--border, #2e2f33);
      border-radius: 8px;
      padding: 12px 14px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
    }
    .toast.success {
      border-left: 4px solid #4caf50;
    }
    .toast.error {
      border-left: 4px solid #f44336;
    }
    .toast.warning {
      border-left: 4px solid #ff9800;
    }
    .toast.info {
      border-left: 4px solid var(--accent, #6ea8fe);
    }
  `;

  @state() private _modals: ActiveNotice[] = [];
  @state() private _toasts: ActiveNotice[] = [];

  connectedCallback(): void {
    super.connectedCallback();
    this._onNotify = this._onNotify.bind(this);
    window.addEventListener(OPERATOR_NOTIFY_EVENT, this._onNotify as EventListener);
  }

  disconnectedCallback(): void {
    window.removeEventListener(OPERATOR_NOTIFY_EVENT, this._onNotify as EventListener);
    super.disconnectedCallback();
  }

  private _onNotify(event: Event): void {
    const detail = (event as CustomEvent<ActiveNotice>).detail;
    if (!detail?.id) return;
    if (detail.modal !== false) {
      this._modals = [...this._modals.filter((m) => m.id !== detail.id), detail];
    } else {
      this._toasts = [...this._toasts.filter((t) => t.id !== detail.id), detail];
      const duration = detail.duration ?? 6000;
      if (duration > 0) {
        setTimeout(() => this._dismiss(detail.id), duration);
      }
    }
  }

  private _dismiss(id: string): void {
    this._modals = this._modals.filter((m) => m.id !== id);
    this._toasts = this._toasts.filter((t) => t.id !== id);
    window.dispatchEvent(new CustomEvent("ead-operator-notify-dismissed", { detail: { id } }));
  }

  private _icon(type: OperatorNotifyType): string {
    switch (type) {
      case "success":
        return "✓";
      case "error":
        return "✕";
      case "warning":
        return "!";
      default:
        return "i";
    }
  }

  render() {
    const modal = this._modals[this._modals.length - 1];
    return html`
      ${this._toasts.length
        ? html`
            <div class="toast-stack">
              ${this._toasts.map(
                (t) => html`
                  <div class="toast ${t.type || "info"}">
                    <div class="header">
                      <div class="title">${this._icon(t.type || "info")} ${t.title}</div>
                      ${t.closable !== false
                        ? html`<button class="close" @click=${() => this._dismiss(t.id)}>×</button>`
                        : ""}
                    </div>
                    <div class="message">${t.message}</div>
                  </div>
                `,
              )}
            </div>
          `
        : ""}
      ${modal
        ? html`
            <div
              class="overlay"
              @click=${(e: Event) => {
                if (e.target === e.currentTarget && modal.closable !== false) this._dismiss(modal.id);
              }}
            >
              <div
                class="dialog ${modal.type || "info"}"
                role="alertdialog"
                aria-labelledby="op-title"
                aria-describedby="op-msg"
                @click=${(e: Event) => e.stopPropagation()}
              >
                <div class="header">
                  <div class="title" id="op-title">${modal.title}</div>
                  ${modal.closable !== false
                    ? html`<button class="close" aria-label="Close" @click=${() => this._dismiss(modal.id)}>×</button>`
                    : ""}
                </div>
                <div class="message" id="op-msg">${modal.message}</div>
                <div class="actions">
                  <button class="btn" @click=${() => this._dismiss(modal.id)}>OK</button>
                </div>
              </div>
            </div>
          `
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-operator-dialog-host": EadOperatorDialogHost;
  }
}
